import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CalendarOccurrence } from '@/entities/board';
import { useCard } from '@/entities/card';
import {
  describeRule,
  usePutOccurrence,
  useRecurrence,
  useResetOccurrence,
  useSplitSeries,
} from '@/entities/recurrence';
import { BulkRecurrenceDeleteDialog, RecurrenceSection } from '@/features/card-details';
import { FormError } from '@/shared/forms';
import { formatCalendarDate } from '@/shared/lib/datetime';
import { draftDuration } from '@/shared/lib/deadline-draft';
import {
  Button,
  Dialog,
  ErrorState,
  LoadingState,
  OverdueToggle,
  RangeGroups,
  TextField,
} from '@/shared/ui';

import styles from './OccurrenceDialog.module.css';

interface Props {
  occurrence: CalendarOccurrence;
  /** The user may change the series (editor or owner). */
  canEdit: boolean;
  onClose: () => void;
  onOpenCard: (cardId: string) => void;
}

type Scope = 'one' | 'following' | 'series';
const SCOPES: readonly Scope[] = ['one', 'following', 'series'];

const clock = (time: string | null) => (time ? time.slice(0, 5) : '');

/**
 * An upcoming occurrence of a recurring series. It is not a card yet and opening it creates
 * none: the person edits either this ONE occurrence (stored as an individual change) or the
 * ENTIRE series (the same editor as in the source card, with its template).
 */
export function OccurrenceDialog({ occurrence, canEdit, onClose, onOpenCard }: Props) {
  const { t, i18n } = useTranslation();

  const [scope, setScope] = useState<Scope>('one');
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  return (
    <Dialog title={occurrence.title} onClose={onClose} size="lg">
      <div className={styles.body} data-testid="occurrence-dialog">
        <p className={styles.badge}>{t('occurrence.badge')}</p>

        <RuleLine sourceCardId={occurrence.source_card_id} />

        <p className={styles.note}>{t('occurrence.virtualNote')}</p>

        {occurrence.has_exception && (
          <p className={styles.changed} role="status">
            {t('occurrence.changed', {
              date: formatCalendarDate(occurrence.original_date, i18n.language),
            })}
          </p>
        )}

        {canEdit && (
          <div role="radiogroup" aria-label={t('occurrence.scopeLabel')} className={styles.scopes}>
            {SCOPES.map((value) => (
              <label key={value} className={styles.scope}>
                <input
                  type="radio"
                  name="occurrence-scope"
                  checked={scope === value}
                  onChange={() => {
                    setScope(value);
                  }}
                />
                {t(`occurrence.scope.${value}`)}
              </label>
            ))}
          </div>
        )}

        {scope === 'one' || !canEdit ? (
          <OneOccurrence occurrence={occurrence} canEdit={canEdit} onDone={onClose} />
        ) : scope === 'following' ? (
          <FollowingOccurrences occurrence={occurrence} onDone={onClose} />
        ) : (
          <SeriesEditor occurrence={occurrence} onOpenCard={onOpenCard} />
        )}

        {canEdit && scope !== 'one' && (
          <div className={styles.actions}>
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                setConfirmBulkDelete(true);
              }}
            >
              {t('recurrence.bulk.open')}
            </Button>
          </div>
        )}
      </div>

      {confirmBulkDelete && scope !== 'one' && (
        <BulkRecurrenceDeleteDialog
          sourceCardId={occurrence.source_card_id}
          scope={scope}
          originalDate={scope === 'following' ? occurrence.original_date : undefined}
          onClose={() => {
            setConfirmBulkDelete(false);
          }}
          onDone={() => {
            setConfirmBulkDelete(false);
            onClose();
          }}
        />
      )}
    </Dialog>
  );
}

function SeriesEditor({
  occurrence,
  onOpenCard,
}: {
  occurrence: CalendarOccurrence;
  onOpenCard: (cardId: string) => void;
}) {
  const { t } = useTranslation();
  const source = useCard(occurrence.source_card_id);
  if (source.isPending) return <LoadingState />;
  if (source.isError) {
    return (
      <>
        <p className={styles.warn}>{t('occurrence.loadError')}</p>
        <ErrorState
          error={source.error}
          onRetry={() => {
            void source.refetch();
          }}
        />
      </>
    );
  }
  return (
    <>
      <RecurrenceSection card={source.data} canEdit mode="series" />
      <div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            onOpenCard(occurrence.source_card_id);
          }}
        >
          {t('recurrence.source')}
        </Button>
      </div>
    </>
  );
}

function OneOccurrence({
  occurrence,
  canEdit,
  onDone,
}: {
  occurrence: CalendarOccurrence;
  canEdit: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const put = usePutOccurrence(occurrence.source_card_id);
  const reset = useResetOccurrence(occurrence.source_card_id);
  const { deadline } = occurrence;
  const initial = {
    title: occurrence.title,
    startDate: deadline.start_date ?? '',
    startTime: clock(deadline.start_time),
    endDate: deadline.date,
    endTime: clock(deadline.time),
  };
  const [form, setForm] = useState(initial);
  const set = (patch: Partial<typeof form>) => {
    setForm({ ...form, ...patch });
  };
  const event = occurrence.is_event;
  const unchanged = JSON.stringify(form) === JSON.stringify(initial);
  const valid =
    form.title.trim() !== '' &&
    form.endDate !== '' &&
    (!event || form.startDate !== '') &&
    (!event || form.startDate <= form.endDate);

  const submit = () => {
    if (unchanged) {
      onDone();
      return;
    }
    put.mutate(
      {
        originalDate: occurrence.original_date,
        overrides: {
          title: form.title.trim(),
          end_date: form.endDate,
          ...(form.endTime ? { end_time: form.endTime } : { clear_end_time: true }),
          ...(event && {
            start_date: form.startDate,
            ...(form.startTime ? { start_time: form.startTime } : { clear_start_time: true }),
          }),
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) submit();
      }}
    >
      <TextField
        label={t('occurrence.fields.title')}
        value={form.title}
        maxLength={500}
        disabled={!canEdit}
        onChange={(e) => {
          set({ title: e.target.value });
        }}
      />
      <RangeGroups
        value={form}
        showStart={event}
        timeOptional
        duration={event && valid ? draftDuration({ ...form, hasStart: true }) : null}
        invalid={{
          startDate: event && form.startDate === '',
          endDate: form.endDate === '' || (event && form.startDate > form.endDate),
        }}
        onChange={set}
      />
      <FormError error={put.error ?? reset.error} />
      {canEdit && (
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={!valid || put.isPending}>
            {t('occurrence.save')}
          </Button>
          <Button
            disabled={put.isPending}
            onClick={() => {
              put.mutate(
                { originalDate: occurrence.original_date, cancelled: true },
                { onSuccess: onDone },
              );
            }}
          >
            {t('occurrence.cancelOne')}
          </Button>
          {occurrence.has_exception && (
            <Button
              variant="ghost"
              disabled={reset.isPending}
              onClick={() => {
                reset.mutate(occurrence.original_date, { onSuccess: onDone });
              }}
            >
              {t('occurrence.reset')}
            </Button>
          )}
        </div>
      )}
      {canEdit && <p className={styles.note}>{t('occurrence.cancelHint')}</p>}
    </form>
  );
}

const dayNumber = (date: string): number => {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
};

/**
 * "This and following": the series is split at this occurrence on the server. Its date is the
 * slot of the schedule and cannot be moved here (an occurrence that was moved is shown in its
 * schedule position); title, times, length and the overdue choice apply from here on.
 */
function FollowingOccurrences({
  occurrence,
  onDone,
}: {
  occurrence: CalendarOccurrence;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const split = useSplitSeries(occurrence.source_card_id);
  const event = occurrence.is_event;
  const { deadline } = occurrence;
  const slot = occurrence.original_date;
  // The occurrence as the SERIES has it (an individual move of this occurrence is not carried over).
  const length =
    event && deadline.start_date ? dayNumber(deadline.date) - dayNumber(deadline.start_date) : 0;
  const lastDay = new Date((dayNumber(slot) + length) * 86_400_000).toISOString().slice(0, 10);
  const initial = {
    title: occurrence.title,
    startDate: event ? slot : '',
    startTime: clock(deadline.start_time),
    endDate: event ? lastDay : slot,
    endTime: clock(deadline.time),
    overdue: deadline.overdue_enabled,
  };
  const [form, setForm] = useState(initial);
  const set = (patch: Partial<typeof form>) => {
    setForm({ ...form, ...patch });
  };
  const valid =
    form.title.trim() !== '' && form.endDate !== '' && (!event || form.startDate <= form.endDate);
  const sameDay = form.endDate === form.startDate;
  const timesOk =
    !event || !sameDay || !form.startTime || !form.endTime || form.endTime >= form.startTime;

  return (
    <form
      className={styles.form}
      noValidate
      data-testid="following-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !timesOk) return;
        split.mutate(
          {
            originalDate: slot,
            template: {
              title: form.title.trim(),
              ...(form.endTime && { end_time: form.endTime }),
              ...(event && {
                ...(form.startTime && { start_time: form.startTime }),
                length_days: dayNumber(form.endDate) - dayNumber(form.startDate),
                overdue_enabled: form.overdue,
              }),
            },
          },
          { onSuccess: onDone },
        );
      }}
    >
      <p className={styles.note}>{t('occurrence.following.note')}</p>
      {occurrence.has_exception && (
        <p className={styles.changed} role="status">
          {t('occurrence.following.replaced')}
        </p>
      )}
      <TextField
        label={t('occurrence.fields.title')}
        value={form.title}
        maxLength={500}
        onChange={(e) => {
          set({ title: e.target.value });
        }}
      />
      <RangeGroups
        value={form}
        showStart={event}
        timeOptional
        fixedDates={{ start: true, end: !event }}
        duration={event && valid && timesOk ? draftDuration({ ...form, hasStart: true }) : null}
        invalid={{ endDate: !valid, endTime: !timesOk }}
        onChange={set}
      />
      {event && (
        <div>
          <OverdueToggle
            value={form.overdue}
            onChange={(overdue) => {
              set({ overdue });
            }}
          />
        </div>
      )}
      <FormError error={split.error} />
      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={!valid || !timesOk || split.isPending}>
          {t('occurrence.following.save')}
        </Button>
      </div>
    </form>
  );
}

/** What the series is, in words (the same text as everywhere: `describeRule`). */
function RuleLine({ sourceCardId }: { sourceCardId: string }) {
  const { t, i18n } = useTranslation();
  const info = useRecurrence(sourceCardId);
  const rule = info.data?.rule;
  if (!rule) return null;
  return (
    <p className={styles.note} data-testid="occurrence-rule">
      {t('calendar.preview.repeats')}: {describeRule(rule, t, i18n.language)}
    </p>
  );
}
